
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SupplierProductModel } from '@/models/supplier_product';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

interface EditableRow {
  supplierProductId: number;
  variantId: number;
  productName: string;
  variant: string;
  sku: string;
  image: string;
  supplierSku: string;
  cost: number | null;
  originalSupplierSku: string;
  originalCost: number | null;
  isDirty: boolean;
}

@Component({ name: 'SupplierCostSkuEditorDialog' })
export default class SupplierCostSkuEditorDialog extends Vue {
  @Prop({ required: true }) supplier!: any;

  private rows: EditableRow[] = [];
  private loading = false;
  private loadError: string | null = null;
  private saving = false;
  private partialErrors: string[] = [];
  private saveError: string | null = null;
  private search = '';
  private showDirtyOnly = false;

  private get dialogMaxWidth(): string {
    return '1200px';
  }

  private get currencyLabel(): string {
    return this.supplier?.currency_code || CurrencyHelper.getStoreCurrency();
  }

  private get headers() {
    return [
      { text: '', value: 'dirty', width: '28px', sortable: false, align: 'center' },
      { text: '', value: 'image', width: '56px', sortable: false },
      { text: 'Product', value: 'productName' },
      { text: 'SKU', value: 'sku', sortable: false, width: '140px' },
      { text: 'Supplier SKU', value: 'supplierSku', sortable: false, width: '180px' },
      {
        text: `Cost (${this.currencyLabel})`,
        value: 'cost',
        sortable: false,
        width: '160px',
        align: 'end'
      }
    ];
  }

  private get dirtyRows(): EditableRow[] {
    return this.rows.filter(r => r.isDirty);
  }

  private get dirtyCount(): number {
    return this.dirtyRows.length;
  }

  private get filteredRows(): EditableRow[] {
    const base = this.showDirtyOnly ? this.dirtyRows : this.rows;
    const q = this.search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(r =>
      r.productName.toLowerCase().includes(q)
      || r.variant.toLowerCase().includes(q)
      || r.sku.toLowerCase().includes(q)
      || r.supplierSku.toLowerCase().includes(q)
    );
  }

  private get rowCountLabel(): string {
    const total = this.rows.length;
    const shown = this.filteredRows.length;
    if (shown === total) return `${total} ${total === 1 ? 'product' : 'products'}`;
    return `${shown} of ${total} products`;
  }

  private get emptyStateText(): string {
    if (this.rows.length === 0) return 'No products assigned to this supplier.';
    if (this.showDirtyOnly && this.dirtyCount === 0) return 'No unsaved changes.';
    if (this.search) return `No products match "${this.search}".`;
    return 'No products to display.';
  }

  private get saveErrorVisible(): boolean {
    return !!this.saveError;
  }
  private set saveErrorVisible(val: boolean) {
    if (!val) this.saveError = null;
  }

  async mounted() {
    await this.loadRows();
  }

  private async loadRows() {
    this.loading = true;
    this.loadError = null;
    try {
      const result = await this.supplier.fetchProductsOptimized({ limit: -1 });
      const incoming: any[] = result.products || [];
      if (incoming.some(item => item.id == null)) {
        console.warn(
          '[SupplierCostSkuEditorDialog] Some products are missing an id field. ' +
          'Restart the API service to pick up the latest supplier_product_service changes.'
        );
      }
      this.rows = incoming.map((item: any) => {
        const cost = item.unit_cost != null ? Number(item.unit_cost) : null;
        const supplierSku = item.supplier_sku || '';
        return {
          supplierProductId: item.id,
          variantId: item.variant_id,
          productName: item['Item[Item Description]'] || '',
          variant: item.variant || '',
          sku: item.product_sku || '',
          image: item.image || '',
          supplierSku,
          cost,
          originalSupplierSku: supplierSku,
          originalCost: cost,
          isDirty: false
        } as EditableRow;
      });
    } catch {
      this.loadError = 'Failed to load products. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  private updateRow(rowId: number, field: 'cost' | 'supplierSku', value: any) {
    const row = this.rows.find(r => r.supplierProductId === rowId);
    if (!row) return;
    if (field === 'cost') row.cost = value as number | null;
    else row.supplierSku = value as string;
    row.isDirty =
      row.cost !== row.originalCost || row.supplierSku !== row.originalSupplierSku;
  }

  private revertAll() {
    this.rows.forEach(row => {
      if (row.isDirty) {
        row.cost = row.originalCost;
        row.supplierSku = row.originalSupplierSku;
        row.isDirty = false;
      }
    });
    this.showDirtyOnly = false;
  }

  private buildSavePayload(): Array<{ id: number; cost: number | null; supplierSku: string }> {
    return this.dirtyRows.map(r => ({
      id: r.supplierProductId,
      cost: r.cost,
      supplierSku: r.supplierSku
    }));
  }

  private handleSaveResult(results: any[], errors: any[]) {
    const successIds = new Set(results.map((r: any) => r.id));
    this.rows.forEach(row => {
      if (successIds.has(row.supplierProductId)) {
        row.isDirty = false;
        row.originalCost = row.cost;
        row.originalSupplierSku = row.supplierSku;
      }
    });
    if (errors.length > 0) {
      this.partialErrors = errors.map((e: any) => {
        const row = this.rows.find(r => r.supplierProductId === (e.update?.id ?? e.id));
        return row ? row.productName : (e.error || 'Unknown product');
      });
    }
  }

  private async save() {
    this.saving = true;
    this.partialErrors = [];
    try {
      const payload = this.buildSavePayload();
      const response: any = await SupplierProductModel.bulkUpdateSettings(payload);
      const results: any[] = response?.results || [];
      const errors: any[] = response?.errors || [];
      this.handleSaveResult(results, errors);
      if (errors.length === 0) {
        this.$emit('saved');
        this.$emit('close');
      } else if (this.dirtyCount === 0) {
        this.showDirtyOnly = false;
      }
    } catch {
      this.saveError = 'Failed to save changes. Please try again.';
    } finally {
      this.saving = false;
    }
  }

  private onCancel() {
    this.$emit('close');
  }

  private getRowClass(item: EditableRow): string {
    return item.isDirty ? 'row--dirty' : '';
  }
}
